import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../ui';
import { useUser } from '../../context/UserContext';
import { supabase } from '../../lib/supabase';
import { uniqueChannelName } from '../../lib/realtime';
import { canMerge, clearBatch, fetchFeedbackBatches, mergeNotes, sendBatchToQueue, type FeedbackBatch } from '../../lib/clientFeedback';

export type Me = { id: string; name: string } | null;

/** Everything the feed needs about feedback: the batches (live), what's picked for a merge, and the batch actions. */
export function useFeedbackFeed(enabled: boolean) {
  const { user } = useUser();
  const toast = useToast();
  const [batches, setBatches] = useState<FeedbackBatch[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const me: Me = user?.id ? { id: user.id, name: user.name || user.email || 'Bullfinch' } : null;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // "Select to merge": the pick boxes (feed rows and panel notes) only show while this is on.
  const [selectMode, setSelectMode] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [merging, setMerging] = useState(false);
  const [justMerged, setJustMerged] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setBatches(await fetchFeedbackBatches()); setError(null); }
    catch (err) { setError((err as Error).message); }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => { if (enabled) void load(); }, [enabled, load]);
  // Live, as the old Feedback screen was: a batch arriving, moving or being withdrawn reloads the feed.
  useEffect(() => {
    if (!enabled) return;
    const ch = supabase
      .channel(uniqueChannelName('client-feedback-batches'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_feedback_batches' }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [enabled, load]);

  // What's picked, from which batches and which app. Notes that left the list (deleted, queued, cleared) drop out.
  const pick = useMemo(() => {
    const from = batches.filter((b) => canMerge(b) && b.items.some((i) => picked.has(i.id)));
    const ids = from.flatMap((b) => b.items.filter((i) => picked.has(i.id)).map((i) => i.id));
    const oldest = [...from].sort((a, c) => (a.sent_at ?? a.received_at).localeCompare(c.sent_at ?? c.received_at))[0] ?? null;
    const sources = new Set(from.map((b) => b.source_id));
    return { ids, from, source: from[0]?.source_id ?? null, sourceLabel: from[0]?.source?.label ?? null, sources: sources.size, oldest };
  }, [batches, picked]);

  const toggle = useCallback((ids: string[], on: boolean) => {
    setConfirmMerge(false);
    setPicked((prev) => {
      const next = new Set(prev);
      for (const id of ids) { if (on) next.add(id); else next.delete(id); }
      return next;
    });
  }, []);
  const clearPicks = useCallback(() => { setPicked(new Set()); setConfirmMerge(false); }, []);
  const selecting = selectMode || picked.size > 0;
  const setSelecting = useCallback((on: boolean) => {
    setSelectMode(on);
    if (!on) clearPicks();
  }, [clearPicks]);

  /** Why this batch can't join the current pick, or null if it can. */
  const pickBlock = useCallback((b: FeedbackBatch): string | null => {
    if (!canMerge(b)) return 'Only New notes that aren’t in the agent queue can be merged';
    if (pick.source && pick.source !== b.source_id) return `Only notes from the same app can be merged (${pick.sourceLabel ?? 'another app'} is picked)`;
    return null;
  }, [pick.source, pick.sourceLabel]);

  const mergeable = pick.ids.length >= 2 && pick.from.length >= 2 && pick.sources === 1;
  const mergeReason = pick.sources > 1
    ? 'Notes from different apps can’t be merged.'
    : pick.from.length < 2 ? 'Pick notes from two or more groups.' : null;

  const merge = async () => {
    if (!mergeable) return;
    setMerging(true);
    try {
      const r = await mergeNotes(pick.ids);
      clearPicks();
      setSelectMode(false);
      setJustMerged(r.batch_id);
      window.setTimeout(() => setJustMerged((m) => (m === r.batch_id ? null : m)), 2600);
      toast.success('Merged into one group. The client’s app shows it the same way.');
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setMerging(false);
    }
  };

  const send = async (b: FeedbackBatch) => {
    if (!me) return;
    setBusy(b.id);
    try {
      await sendBatchToQueue(b, me);
      toast.success('Sent to the agent queue');
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const clear = async (b: FeedbackBatch, on: boolean) => {
    if (!me) return;
    try { await clearBatch(b.id, me.id, on); await load(); }
    catch (err) { toast.error((err as Error).message); }
  };

  return {
    batches, loaded, error, load, me, busy, send, clear,
    picked, pick, toggle, clearPicks, selecting, setSelecting, pickBlock, mergeable, mergeReason, confirmMerge, setConfirmMerge, merging, merge, justMerged,
  };
}

export type FeedbackFeed = ReturnType<typeof useFeedbackFeed>;
